import React, {ReactNode, useEffect, useState, useRef, useCallback} from "react";
import {User} from "../../../shared/client-server/types";
import axios from "axios";
import {AuthenticationContextType, AuthenticationContext} from "./AuthenticationContext";

export const AuthenticationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isRefreshingRef = useRef(false);
    
    // Manually refresh the token and manage scheduling
    const refreshAccessToken = async () => {
        // Don't try to refresh if there's no user logged in
        if (!user) return;

        // Prevent concurrent refresh attempts
        if (isRefreshingRef.current) return;
        isRefreshingRef.current = true;

        try {
            console.log('Refreshing access token...');

            const response = await axios.post('/api/auth/refresh-token');
            setAccessToken(response.data.accessToken);
            
            console.log('Token refreshed successfully');
        } catch (error) {
            console.error('Failed to refresh token:', error);
            // On critical errors, we might want to log out the user, but that's
            // typically handled by the response interceptor
        } finally {
            isRefreshingRef.current = false;
        }
    };
    
    // Setup axios and user session
    useEffect(() => {
        // Configure axios defaults
        axios.defaults.withCredentials = true;

        const requestInterceptor = axios.interceptors.request.use(
            config => {
                if (accessToken) {
                    config.headers.Authorization = `Bearer ${accessToken}`;
                }
                return config;
            },
            error => Promise.reject(error)
        );

        const responseInterceptor = axios.interceptors.response.use(
            response => response,
            async error => {
                const originalRequest = error.config;

                // If error is 401 and not a retry attempt
                if (error.response?.status === 401 &&
                    error.response?.data?.expired &&
                    !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        // Call refresh token endpoint
                        const response = await axios.post(`/api/auth/refresh-token`);
                        setAccessToken(response.data.accessToken);
                        
                        // Retry the original request
                        originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
                        return axios(originalRequest);
                    } catch (refreshError) {
                        // If the refresh fails, log out the user
                        setUser(null);
                        setAccessToken(null);
                        clearTokenRefresh();
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );

        // Function to load user on page load
        const loadUser = async () => {
            try {
                const response = await axios.get('/api/auth/me');
                setUser(response.data.user);

                if (response.data.accessToken) {
                    setAccessToken(response.data.accessToken);
                }
            } catch (error) {
                console.error('Failed to load user:', error);
                setUser(null);
                setAccessToken(null);
            } finally {
                setIsLoading(false);
            }
        };

        loadUser();

        // Clean up interceptors and timeout on unmount
        return () => {
            // Clean up interceptors and timeouts
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
            clearTokenRefresh();
        };
    }, []); // Empty dependency array - this effect runs once

    const clearTokenRefresh = () => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }
    };

    const scheduleTokenRefresh = () => {
        clearTokenRefresh();

        if (user) {
            console.log('Scheduling token refresh for 10 minutes from now');

            refreshTimeoutRef.current = setTimeout(() => {
                refreshAccessToken();
            }, 10 * 60 * 1000);
        }
    };

    useEffect(() => {
        if (accessToken && user) {
            scheduleTokenRefresh();
        }
    }, [accessToken, user]);

    const login = async (email: string, password: string): Promise<boolean> => {
        try {
            const response = await axios.post(`/api/auth/login`, {
                email,
                password
            });

            // Store user and token in state only
            setUser(response.data.user);
            setAccessToken(response.data.accessToken);

            return true;
        } catch (error) {
            console.error('Login failed:', error);
            return false;
        }
    };
    
    const logout = async (): Promise<void> => {
        try {
            await axios.post(`/api/auth/logout`);
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            setUser(null);
            setAccessToken(null);

            clearTokenRefresh()
        }
    };
    
    const logoutAll = async (): Promise<void> => {
        try {
            await axios.post(`/api/auth/logout-all`);
        } catch (error) {
            console.error('Logout all failed:', error);
        } finally {
            setUser(null);
            setAccessToken(null);
            
            clearTokenRefresh()
        }
    };

    const value: AuthenticationContextType = {
        user,
        accessToken,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        logoutAll
    };

    return (
        <AuthenticationContext.Provider value={value}>
            {children}
        </AuthenticationContext.Provider>
    );
};